<template>
  <div class="billing-apple-external16218">
    <LoadingSpinner v-if="wait.is('Loading Billing')" />

    <div
      v-else-if="!wait.is('Error Loading Billing') && billing && plan"
      class="billing-apple-external16218__inner"
    >
      <AppButton
        v-if="showRedirectButton"
        class="billing-apple-external16218__redirect-button"
        href="shortform://?close-browser16218"
        >Back to the App</AppButton
      >
      <SubscribePopup
        ref="paymentForm"
        class="billing-apple-external16218__form"
        :billing="billing"
        :plan="plan"
        :fullscreen16218="true"
        @subscribed="showRedirectButton = true"
      />
    </div>

    <LoadingError v-else />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRoute } from 'vue-router'

import { createBilling } from '@/models/billing'
import type { BillingView, PlanView } from '@/models/billing'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import SubscribePopup from '@/app/components/billing/SubscribePopup.vue'
import AppButton from '@/app/components/button/AppButton.vue'

const route = useRoute()
const wait = useWait()

const paymentForm = ref<InstanceType<typeof SubscribePopup> | null>(null)

const billing = ref<Raw<BillingView> | null>(null)
const plan = ref<Raw<PlanView> | null>(null)

const showRedirectButton = ref(false)

onBeforeMount(async () => {
  await refreshBilling()
})

async function refreshBilling(): Promise<void> {
  wait.start('Loading Billing')
  wait.end('Error Loading Billing')

  try {
    const response = await backend.getBilling()
    billing.value = await createBilling(response)

    // The plan's ID is set in `BillingApplePriceComparison`,
    // from where we open this page. We cast it as any
    // and let `getPlanByID` throw if it's invalid.
    const planID = route.query.plan16218 as any
    plan.value = billing.value.getPlanByID(planID)
  } catch (error) {
    wait.start('Error Loading Billing')
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['billing-loading-error'])
      Sentry.captureException(error)
    })
  }
  wait.end('Loading Billing')

  // The payment form is hidden by default (it's rendered
  // inside a popup). We have to wait for the component to
  // be mounted, and then show it.
  await nextTick()
  if (paymentForm.value) {
    paymentForm.value.show()
  }
}
</script>

<style scoped lang="scss">
.billing-apple-external16218 {
  height: 100%;
  padding: var(--container-padding);

  &__inner {
    display: grid;
    place-items: center;
    gap: 42px;
    height: 100%;
  }

  &__redirect-button {
    width: fit-content;
    animation: fadeIn 0.3s 1.5s ease-in-out;
  }

  &__form {
    position: static;
    height: auto;
    padding: 0;

    :deep(.popup__window) {
      width: 100%;
      max-height: none;
      max-width: none;
      border-radius: 0;
      box-shadow: none;
      overflow: visible;
    }

    :deep(.popup__close) {
      display: none;
    }

    :deep(.popup__title) {
      font-size: 24px;
      text-align: center;
    }
  }
}
</style>
